import { IssueFieldEnum } from "@mcapps/shared/enums";
import { IMapOfEvaluationCategoriesApi, IRisikoProbability } from "@mcapps/shared";
import { IIssueApi } from "@schemas/issueSchema";
import { ColDef, ColDefField, ICellRendererParams, ValueFormatterParams } from "ag-grid-community";
import React from "react";
import { getMatrixItemAndScore } from "@mcapps/shared/sharedUtils";
import { ISSUE_SECTIONS } from "../../../issueConstants";
import { IssueTableColumnContext } from "./issueTableColumnContext";

export const getRisikoEvaluationColumns = ({
  columnContext,
}: {
  columnContext: IssueTableColumnContext;
}): ColDef<any>[] => {
  const { getIssueColumnBaseDef, hideAndSort, issueBoard } = columnContext;
  const categoriesFromBoard: IMapOfEvaluationCategoriesApi =
    issueBoard?.risikoSection?.evaluationCategoriesConfig ?? {};
  const evaluationCategories = Object.entries(categoriesFromBoard)
    .filter(([, value]) => value.isActive)
    .map(([key, value]) => ({
      key,
      ...value,
    }));
  const probabilityColumn: ColDef<any> = {
    ...getIssueColumnBaseDef(IssueFieldEnum.RISK_PROBABILITY),
    editable: true,
    context: ISSUE_SECTIONS.RISIKO,
    valueFormatter: (params) => {
      const risikoItem = issueBoard?.risikoSection?.probabilites[params.value];
      return risikoItem?.name || "";
    },
    filter: "agFilter",
    cellEditor: "agRichSelectCellEditor",
    cellEditorParams: {
      values: Object.keys(issueBoard?.risikoSection?.probabilites || {}),
      valueFormatter: (params) => {
        const risikoItem = issueBoard?.risikoSection?.probabilites?.[params.value];
        return risikoItem?.name || "";
      },
    },
  };

  const evaluationColumns: ColDef<any>[] = evaluationCategories.flatMap((category) => {
    const consequenceColDef: ColDef<any> = {
      field: `risiko.consequence.${category.key}`,
      headerName: `${category?.name} konsekvens`,
      headerClass: "cell-px2 cell-py0 two-line-header",
      filter: "agFilter",
      editable: true,
      cellEditor: "agRichSelectCellEditor",
      valueFormatter: (params: ValueFormatterParams<IIssueApi>) => {
        const risikoItem: IRisikoProbability = categoriesFromBoard?.[category.key]?.consequences[params.value];
        return risikoItem?.name || "";
      },
      cellEditorParams: {
        values:
          Object.entries(categoriesFromBoard[category.key]?.consequences ?? {})
            .filter(([, obj]) => obj.isActive)
            .map(([id]) => id) ?? [],
        valueFormatter: (params) => {
          const risikoItem = categoriesFromBoard?.[category.key]?.consequences?.[params.value];
          return risikoItem?.name || "";
        },
      },
      context: ISSUE_SECTIONS.RISIKO,
      ...hideAndSort(`risiko.consequence.${category.key}`),
    };

    const matrixValueColDef: ColDef<IIssueApi> = {
      field: `risikoMatrixValue.${category.key}` as ColDefField<IIssueApi>,
      headerName: `${category.name} poeng`,
      headerClass: "cell-px2 cell-py0 two-line-header",
      wrapHeaderText: true,
      initialWidth: 100,
      filter: "agNumberColumnFilter",
      valueGetter: (params) => {
        try {
          const matrixItemAndScore = getMatrixItemAndScore({
            issue: params.data,
            issueBoard,
            evaluationCategoryCapital: category.key,
          });
          return matrixItemAndScore?.score;
        } catch (error) {
          return 0;
        }
      },
      cellRenderer: (params: ICellRendererParams<IIssueApi>) => {
        try {
          const matrixItemAndScore = getMatrixItemAndScore({
            issue: params.data,
            issueBoard,
            evaluationCategoryCapital: category.key,
          });

          return (
            <div
              className="flex-items-center"
              style={{
                display: "flex",
                color: matrixItemAndScore?.matrixItem?.color != null ? "white" : "black",
                backgroundColor: matrixItemAndScore?.matrixItem?.color,
                fontSize: "12px",
                borderRadius: "5px",
                width: "100%",
                justifyContent: "center",
              }}
            >
              {matrixItemAndScore?.score}
            </div>
          );
        } catch (error) {
          return <div>-</div>;
        }
      },
      context: ISSUE_SECTIONS.RISIKO,
      ...hideAndSort(`risikoMatrixValue.${category.key}`),
    };

    return [consequenceColDef, matrixValueColDef];
  });

  const categoryCommentCol: ColDef<IIssueApi> = {
    field: `risiko.categoryComment`,
    headerName: "Risiko kommentar",
    headerClass: "cell-px2 cell-py0 two-line-header",
    wrapHeaderText: true,
    initialWidth: 180,
    minWidth: 120,
    filter: "agTextColumnFilter",
    editable: true,
    context: ISSUE_SECTIONS.RISIKO,
    ...hideAndSort(`risiko.categoryComment`),
  };
  const pointTotalCol: ColDef<any> = {
    field: "risiko.pointTotal",
    headerName: "Total poengsum",
    headerClass: "cell-px2 cell-py0 two-line-header",
    wrapHeaderText: true,
    filter: "agNumberColumnFilter",
    context: ISSUE_SECTIONS.RISIKO,
    ...hideAndSort(`risiko.pointTotal`),
    valueGetter: (params) => {
      try {
        const totalPoints = evaluationCategories.reduce((sum, category) => {
          const value = Number(params.getValue(`risikoMatrixValue.${category.key}`));
          return sum + (value ? Number(value) : 0);
        }, 0);
        return totalPoints;
      } catch (error) {
        return 0;
      }
    },
  };
  const lowestPointCol: ColDef<any> = {
    field: "risiko.worstScore",
    headerName: "Verste kategori",
    headerClass: "cell-px2 cell-py0 two-line-header",
    filter: "agNumberColumnFilter",
    wrapHeaderText: true,
    context: ISSUE_SECTIONS.RISIKO,
    ...hideAndSort(`risiko.worstScore`),
    valueGetter: (params) => {
      try {
        const highestPoint = evaluationCategories.reduce((max, category) => {
          const value = Number(params.getValue(`risikoMatrixValue.${category.key}`));
          return Math.max(max, value || 0);
        }, 0);
        return highestPoint;
      } catch (error) {
        return 0;
      }
    },
  };

  return [probabilityColumn, ...evaluationColumns, pointTotalCol, lowestPointCol, categoryCommentCol];
};
